import { useEffect, useMemo, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useEditMode,
  usePage,
} from '@vibesites/runtime/react';

// ============================================================================
// VirtualTourEmbed — adapted from AION Management Stadium House Theme.
//
// When the property has no tours with a URL, redirect back to /floor-plans
// so /virtual-tour effectively "doesn't exist" for those properties. Behavior
// with tours: dropdown selector (when multiple) + inline iframe embed.
//
// Guards:
//  - SSR: `typeof window === 'undefined'` short-circuits, so static HTML
//    still ships the fallback message (no redirect during build).
//  - Editor: `useEditMode().isEditMode` is true inside the inline editor
//    iframe, so authors can still open and preview the page there.
//  - Loading: `tours === undefined` (collection still resolving) waits a
//    tick before deciding — we only redirect once we have a concrete empty
//    result.
// ============================================================================

interface VirtualTour {
  virtual_tour_title?: string | null;
  virtual_tour_url?: string | null;
}

const REDIRECT_TARGET = '/floor-plans';

export function VirtualTourEmbed() {
  const tours = useCollectionBinding<VirtualTour>('virtualtourembed.tours', {
    collection: 'virtual_tours',
  });

  const editMode = useEditMode();
  const pageCtx = usePage();

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const validTours = useMemo(
    () => (tours || []).filter((t) => t.virtual_tour_url),
    [tours],
  );

  // Only redirect once the collection has resolved to an empty result AND
  // we're actually on the standalone /virtual-tour page in a real browser
  // (not SSR, not the inline editor iframe). Any of these being false leaves
  // the "No virtual tours available" fallback in place.
  const shouldRedirect =
    tours !== undefined
    && validTours.length === 0
    && !editMode?.isEditMode
    && pageCtx?.alias === 'virtual-tour';

  useEffect(() => {
    if (!shouldRedirect) return;
    if (typeof window === 'undefined') return;
    setIsRedirecting(true);
    window.location.replace(REDIRECT_TARGET);
  }, [shouldRedirect]);

  // While the redirect is in-flight (or we've determined we're about to
  // redirect), render nothing so the empty-state message doesn't flash on
  // screen. The hero above still shows briefly — that's acceptable given
  // this is a client-side bounce triggered on mount.
  if (isRedirecting || shouldRedirect) {
    return null;
  }

  const activeTour = validTours[selectedIndex] || validTours[0];
  const hasMultiple = validTours.length > 1;

  return (
    <section className="bg-white py-8 md:py-12">
      {/* Tour selector */}
      {hasMultiple && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-6 flex justify-end">
          <div className="relative w-full sm:w-auto sm:min-w-[18rem]">
            <select
              value={selectedIndex}
              onChange={(e) => setSelectedIndex(Number(e.target.value))}
              className="appearance-none w-full font-body text-base bg-[var(--vibe-color-primary)] text-[var(--vibe-color-text-on-primary)] rounded-full pl-8 pr-14 py-4 focus:outline-none focus:ring-2 focus:ring-[var(--vibe-color-primary)]/30 cursor-pointer truncate"
            >
              {validTours.map((tour, i) => (
                <option
                  key={i}
                  value={i}
                  className="bg-white text-[var(--vibe-color-text)]"
                >
                  {tour.virtual_tour_title || `Tour ${i + 1}`}
                </option>
              ))}
            </select>
            <svg
              aria-hidden="true"
              viewBox="0 0 20 20"
              fill="none"
              className="pointer-events-none absolute right-6 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--vibe-color-text-on-primary)]"
            >
              <path
                d="M5 7.5L10 12.5L15 7.5"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
      )}

      {/* Tour embed */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {activeTour?.virtual_tour_url ? (
          <div className="relative w-full rounded-lg overflow-hidden border border-[var(--vibe-color-border)]" style={{ paddingBottom: '56.25%' }}>
            <iframe
              key={activeTour.virtual_tour_url}
              src={activeTour.virtual_tour_url}
              className="absolute inset-0 w-full h-full border-0"
              allowFullScreen
              title={activeTour.virtual_tour_title || 'Virtual Tour'}
            />
          </div>
        ) : (
          <div className="aspect-video w-full bg-[var(--vibe-color-surface)] rounded-lg flex items-center justify-center">
            <p className="text-[var(--vibe-color-text-muted)] font-body">
              No virtual tours are available for this property.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'VirtualTourEmbed',
  displayName: 'Virtual Tour',
  tokens: {},
  collectionBindings: {
    'virtualtourembed.tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(VirtualTourEmbed as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default VirtualTourEmbed;
